import React, { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

import styles from '../quote/quote.module.css';

const Quote = ({ quotes, image, hideButtons = false }) => {
  const [opacities, setOpacities] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [slideCount, setSlideCount] = useState(0);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      slides: quotes.length,
      loop: true,
      breakpoints: {
        '(min-width: 640px)': {
          defaultAnimation: {
            duration: 1500
          }
        }
      },
      defaultAnimation: {
        duration: 300
      },
      created(slider) {
        setLoaded(true);
        setSlideCount(slider.slides.length);
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      detailsChanged(slider) {
        const newOpacities = slider.track.details.slides.map(slide => slide.portion);
        setOpacities(newOpacities);
      }
    },
    [
      slider => {
        let timeout;
        let mouseOver = false;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver || slider.slides.length <= 1) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 4000);
        }
        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      }
    ]
  );

  return (
    <div className="z-10 sm:space-y-12">
      <div className="relative overflow-hidden rounded-xl bg-codGray">
        <div className="h-[4px] w-full bg-gradient-x absolute bottom-0 sm:hidden" />
        <div className={cn('absolute inset-0 rounded-2xl hidden sm:block', styles.bg)}>
          <div className={cn('h-full', styles.bgGrid)} />
        </div>
        <div ref={sliderRef} className="keen-slider">
          {quotes.map((quote, idx) => (
            <div
              className="flex flex-col gap-8 transition-opacity duration-100 ease-in-out keen-slider__slide lg:flex-row lg:flex-nowrap lg:items-center"
              style={{ opacity: opacities[idx] }}
              key={quote.logo.alt}
            >
              <BlockQuote quote={quote} image={image} />
            </div>
          ))}
        </div>
        {loaded && instanceRef.current && slideCount > 1 && (
          <div className="absolute w-full flex items-center justify-center bottom-[13px] z-10">
            <div className="flex justify-center space-x-2 transform bottom-4 max-w-max">
              {[...Array(instanceRef.current.track.details.slides.length).keys()].map(idx => (
                <span
                  key={idx}
                  onClick={() => {
                    instanceRef.current.moveToIdx(idx);
                  }}
                  className={cn(
                    'h-6 w-6 cursor-pointer flex items-center',
                    currentSlide === idx ? 'opacity-100' : 'opacity-30'
                  )}
                >
                  <hr className="h-[2px] w-full border-white sm:border-black border-t-2" />
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {loaded && instanceRef.current && slideCount > 1 && !hideButtons && (
        <div
          className={cn(
            'w-full sm:flex items-center hidden h-full min-h-[40px]',
            quotes.length > 3 ? 'justify-evenly' : 'justify-center gap-4'
          )}
        >
          {quotes.map((quote, idx) => (
            <div
              key={quote.logo.alt}
              onClick={() => {
                instanceRef.current.moveToIdx(idx);
              }}
              className={cn(
                'cursor-pointer flex justify-center align-middle items-center relative',
                currentSlide === idx ? 'opacity-100' : 'opacity-30'
              )}
            >
              <Image
                className="object-fill max-h-10 max-w-[78%] aspect-[28/10]"
                loading="eager"
                quality={100}
                width={quote.logo.width}
                height={quote.logo.height}
                src={quote.logo.src}
                style={{ filter: 'contrast(0) brightness(100)' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BlockQuote = ({ quote, image }) => (
  <>
    {image && (
      <div className={cn('absolute h-full w-1/2 right-0 opacity-30 hidden sm:block', styles.bgImg)}>
        <Image
          fill
          quality={100}
          src={quote.img.src}
          className="object-cover mix-blend-luminosity grayscale"
        />
      </div>
    )}
    <div className="box-content relative z-10 flex w-auto p-6 pt-8 pb-0 mx-auto mb-8 sm:mb-0 sm:order-2 min-w-min sm:mx-0 lg:mx-auto sm:p-20 sm:pt-0 lg:pb-0 lg:justify-center sm:opacity-80">
      {quote.logo && (
        <div className="relative sm:w-[160px] lg:w-[180px] aspect-[3/2] sm:aspect-[3/2] w-[45%] sm:max-w-none">
          <Image
            loading="eager"
            fill
            quality={100}
            src={quote.logo.src}
            className={cn(styles.quoteBrand, 'object-contain')}
          />
        </div>
      )}
    </div>
    <div className="lg:flex-1 max-w-[780px] px-10 pb-14 sm:p-20 sm:pb-0 lg:pb-20 lg:pr-0 text-black text-left">
      <blockquote className="mb-8 text-lg text-white sm:text-black sm:text-base md:text-xl">
        {quote.content}
      </blockquote>
      <h4 className="inline-block mb-2 text-lg leading-10 sm:font-medium sm:leading-7 sm:mb-0 text-gradient sm:text-no-gradient">
        {quote.profile}
      </h4>
      <p className="text-lg text-silver sm:text-black sm:text-base">{quote.position}</p>
    </div>
  </>
);

Quote.defaultProps = {
  quotes: [
    {
      logo: {
        src: '/static/images/pages/homepage/quotes/nuro.svg',
        alt: 'Nuro'
      },
      content:
        "“We really value our partnership with Scale and their ability to deliver custom workflows that suit our needs across a variety of mapping and deep learning models. Scale's high-quality, rapidly labeled data will enable us to ramp our map expansion, allowing us to provide more service to customers like Dominos.”",
      profile: 'Eleonor Concepcion',
      position: 'Software Operations Program Manager, Nuro'
    }
  ],
  image: false
};

export default Quote;
